import type { ClusterStatus, ClusterStatusAdministrativo } from '../utils/clusterStatus'
import api, { apiClient } from './api'

// Cluster interfaces
export interface ClusterResource {
  total: number
  allocated: number
  reserved: number
  available: number
  utilization: number
  unit: string
}

export interface Cluster {
  id: string
  name: string
  description?: string
  status: ClusterStatus // os 8 do domínio — ver `utils/clusterStatus.ts` (#878)
  availability_zone: string
  sdn_enabled: boolean
  network_config?: Record<string, any>
  wan_bridge?: string // Bridge for WAN/public network (e.g. vmbr0)
  // false = a rede WAN deste cluster nao entrega DHCP; Cloud Gateway precisa
  // de IP fixo (do pool publico ou informado).
  wan_dhcp_available?: boolean
  wan_vlan_tag?: number | null // VLAN tag 802.1q da WAN (#1069); null = sem tag
  vm_storage_pool?: string // Default storage pool for VMs
  template_storage_pool?: string // Storage pool for templates
  storage_config?: Record<string, any> // Additional storage configuration
  soft_delete_enabled?: boolean
  soft_delete_retention_hours?: number
  last_sync?: string
  proxmox_version?: string
  total_cpu_cores?: number
  total_memory_gb?: number
  total_storage_gb?: number
  resources: Record<string, ClusterResource>
  created_at: string
  updated_at: string
}

// Request for creating cluster via agent
export interface CreateClusterViaAgentRequest {
  name: string
  description?: string
  availability_zone?: string
  wan_bridge?: string
}

// Registration token information
export interface RegistrationTokenInfo {
  id: string
  token: string
  expires_at: string
  max_uses: number
  remaining_uses: number
  used_count?: number
  is_valid?: boolean
  created_at?: string
}

// Response from creating cluster via agent
export interface ClusterCreationResponse {
  cluster: Cluster
  registration_token: RegistrationTokenInfo
  installation_commands: string[]
}

// Response from getting cluster registration token.
// A fonte unica dos comandos e o backend (app.services.agent_install): alem do
// array flat (`installation_commands`, exibido em bloco unico), o backend expoe as
// peças nomeadas que o modal fatiado da AgentsPage/AgentsTab consome — nenhuma tela
// remonta o comando no cliente.
export interface ClusterRegistrationTokenResponse {
  cluster_id: string
  cluster_name: string
  registration_token: RegistrationTokenInfo
  installation_commands: string[]
  // opcionais: durante a janela de rollout (CF Pages e Fly deployam separado) o backend
  // antigo devolve o payload sem estas chaves — o hook e defensivo (`?.`/`??`).
  proxmox_setup_commands?: string[] // pveum: cria usuario + token de API no Proxmox
  configure_command?: string[] // `nebula-agent configure ...` (host gRPC + token real)
  server_host?: string // host gRPC (NAO a API REST)
}

export interface UpdateClusterRequest {
  name?: string
  description?: string
  // Só os 3 administrativos: o backend responde 422 para qualquer outro, inclusive um
  // estado de máquina do próprio domínio (`online`). Omitir preserva o que está gravado —
  // `statusDoClusterParaEnvio` decide (#878).
  status?: ClusterStatusAdministrativo
  availability_zone?: string
  sdn_enabled?: boolean
  network_config?: Record<string, any>
  wan_bridge?: string
  wan_dhcp_available?: boolean
  wan_vlan_tag?: number | null // VLAN tag 802.1q da WAN (#1069); null = sem tag
  vm_storage_pool?: string | null // storage padrão das VMs; null limpa, omitido não altera (#875)
  template_storage_pool?: string | null // storage dos templates; null limpa (#875)
  storage_config?: Record<string, any> // Additional storage configuration
  soft_delete_enabled?: boolean
  soft_delete_retention_hours?: number
}

export interface SyncResponse {
  success: boolean
  message: string
  synced_at?: string
  nodes_synced?: number
  resources_updated?: number
  inventory_updates?: number
  cluster_info?: Record<string, any>
  /** A coleta terminou, mas faltou algum node — a capacidade lida está incompleta. */
  partial?: boolean
  /** Nodes que não responderam durante o sync. */
  failed_nodes?: string[]
  error?: string
}

// Node interfaces
export interface ClusterNode {
  id: string
  name: string
  status: string
  uptime: number
  cpu_cores: number
  cpu_usage: number
  /** MB — legado (#1177); a tela usa `memory_*_bytes`. */
  memory_total: number
  memory_used: number
  /** BYTES. Ausente em resposta antiga do cache; null quando o node não foi lido. */
  memory_total_bytes?: number | null
  memory_used_bytes?: number | null
  memory_usage: number
  /** GB, soma de TODOS os storages (inclusive compartilhados) — legado (#1177). */
  storage_total: number
  storage_used: number
  storage_usage: number
  /** BYTES do storage LOCAL do node (shared=0, ativo, habilitado). Compartilhado fica de fora. */
  local_storage_total_bytes?: number | null
  local_storage_used_bytes?: number | null
  /** Percentual local; null quando o node não tem storage local ou não foi lido. */
  local_storage_usage?: number | null
  network_in: number
  network_out: number
  vm_count: number
  load_average: number[]
  /** °C; null = não medido (o PVE não expõe a temperatura). */
  temperature: number | null
  kernel_version: string
  pve_version: string
}

export interface NodesResponse {
  cluster_id: string
  cluster_name: string
  nodes: ClusterNode[]
  total_nodes: number
  online_nodes: number
  total_vms: number
  last_updated: string
}

// VM interfaces
export interface ClusterVM {
  vmid: number
  name: string
  node: string
  status: string
  uptime: number
  cpu_cores: number
  cpu_usage: number
  memory_mb: number
  memory_usage: number
  disk_gb: number
  disk_usage: number
  network_in: number
  network_out: number
  template: boolean
  os_type: string
  /** Sempre nulo desde a #1006: a listagem vem do `/cluster/resources`, que não traz IP —
   *  e o valor anterior ("agent-available"/"unknown") era placeholder que nunca virou dado. */
  ip_address: string | null
  qemu_version: string
  tags?: string
  is_phantom?: boolean
  customer_org_name?: string | null
  reseller_org_name?: string | null
  pending_deletion?: boolean
  scheduled_deletion_at?: string | null
  // Propriedade (#999): `vm_instance_id` nulo = VM sem registro no sistema.
  vm_instance_id?: string | null
  customer_org_id?: string | null
  reseller_org_id?: string | null
  has_owner?: boolean
  // Só uma VM sem dono e que NÃO é infraestrutura da plataforma pode ser adotada.
  adoptable?: boolean
  infra_kind?:
    | 'cloud_gateway'
    | 'relay'
    | 'block_storage_phantom'
    | 'cluster_template'
    | 'template'
    | null
  ownership_transfer?: {
    id: string
    kind: 'adoption' | 'transfer'
    status: 'in_progress' | 'failed'
    step: 'preflight' | 'network' | 'database' | 'done'
    error: string | null
    created_at: string | null
  } | null
}

export interface VMsResponse {
  cluster_id: string
  cluster_name: string
  vms: ClusterVM[]
  total_vms: number
  running_vms: number
  stopped_vms: number
  filters: {
    node?: string
    status?: string
  }
  last_updated: string
}

// Proxmox Template interfaces
export interface ProxmoxTemplate {
  vmid: number
  name: string
  node: string
  cpu_cores: number
  memory_mb: number
  disk_gb: number
  os_type: string
  tags: string[]
  disks: Array<{
    interface: string
    storage: string
    info: string
  }>
  description: string
  created_at: string | null
  source: 'platform' | 'custom'
  proxmox_template_id: string | null

  // Inspeção para o fluxo de registro (BYO)
  /** Sem drive cloud-init a VM sobe sem rede e sem credencial — registro bloqueado */
  has_cloud_init: boolean
  /** Storage do disco de boot */
  disk_storage: string | null
  /** null = indeterminado; false prende o provisionamento ao node do template */
  storage_is_shared: boolean | null
  /** Sugestões para o CP confirmar; null quando o ostype não permite inferir */
  suggested_category: string | null
  suggested_os_type: string | null
  suggested_os_variant: string | null
  /** Já promovido ao catálogo (baixado ou registrado) */
  is_registered: boolean
}

export interface ProxmoxTemplatesResponse {
  cluster_id: string
  cluster_name: string
  templates: ProxmoxTemplate[]
  total_templates: number
  data_source: string
  last_updated: string
}

// Storage interfaces
export interface StoragePool {
  storage_id: string
  node: string
  type: string
  content: string[]
  total_gb: number
  used_gb: number
  available_gb: number
  usage_percent: number
  path?: string
  pool?: string
  server?: string
  export?: string
  vgname?: string
  monhost?: string
  enabled: boolean
  shared: boolean
}

export interface StorageResponse {
  cluster_id: string
  cluster_name: string
  storage_pools: StoragePool[]
  summary: {
    total_pools: number
    total_capacity_gb: number
    used_capacity_gb: number
    available_capacity_gb: number
    usage_percent: number
    shared_pools: number
    enabled_pools: number
  }
  /** Coleta incompleta: algum nó não respondeu. O que veio é verdade, mas falta storage. */
  partial?: boolean
  /** Nós que não responderam (quando `partial`). */
  failed_nodes?: string[]
  last_updated: string
}

// Monitoring interfaces
export interface MetricDataPoint {
  timestamp: string
  value: number
}

export interface MonitoringResponse {
  cluster_id: string
  cluster_name: string
  timeframe: string
  data_points: number
  metrics: Record<string, MetricDataPoint[]>
  last_updated: string
  available_metrics: string[]
}

// Logs interfaces
export interface LogEntry {
  timestamp: string | null
  /** `INFO` | `WARNING` | `ERROR` | `DEBUG` — o que `get_cluster_logs` do backend emite. */
  level: string
  node: string
  service: string
  message: string
  pid: number | null
}

export interface LogsResponse {
  cluster_id: string
  cluster_name: string
  /** Só o que o Proxmox devolveu. Lista vazia é "sem entradas (no filtro)", nunca
   *  fallback: o backend não fabrica mais logs (#767) — falha de leitura chega como
   *  erro HTTP, e a aba a mostra como tal. */
  logs: LogEntry[]
  /** Quantas entradas CASARAM com o filtro (antes do `limit`). */
  total_entries: number
  filtered_entries: number
  /** Quantas o cluster tem, sem filtro. */
  all_entries: number
  filters: {
    level?: string
    node?: string
    service?: string
    limit: number
  }
  available_filters: {
    levels: string[]
    nodes: string[]
    services: string[]
  }
  last_updated: string
}

// Network interfaces
export interface SDNZone {
  zone: string
  type: string
  nodes?: string
  ipam?: string
  dns?: string
  reversedns?: string
  dnszone?: string
}

export interface SDNVNet {
  vnet: string
  zone: string
  tag?: number
  alias?: string
  vlanaware?: boolean
}

export interface NetworkBridge {
  iface: string
  type: string
  active: boolean
  autostart: boolean
  bridge_ports?: string
  bridge_stp?: string
  bridge_fd?: string
  cidr?: string
  gateway?: string
  address?: string
  netmask?: string
}

export interface NetworksResponse {
  cluster_id: string
  cluster_name: string
  availability_zone: string
  sdn_enabled: boolean
  saved_network_config?: Record<string, any>
  proxmox_networks: {
    sdn_zones: SDNZone[]
    sdn_vnets: SDNVNet[]
    bridges_by_node: Record<string, NetworkBridge[]>
    nodes: string[]
    error?: string
  }
  last_updated: string
}

// VM Action interfaces
export interface VMActionResponse {
  cluster_id: string
  vmid: number
  action: string
  status: string
  message: string
  timestamp: string
  task_id: string
}

// Compatibility Check interfaces
export interface CompatibilityCheck {
  name: string
  status: 'pass' | 'warning' | 'fail' | 'error'
  message: string
  details: Record<string, any>
  warnings: string[]
  timestamp: string
}

export interface CompatibilityReport {
  overall_status: 'excellent' | 'good' | 'warning' | 'critical' | 'checking'
  compatibility_score: number
  total_checks: number
  passed_checks: number
  failed_checks: number
  warnings: string[]
  errors: string[]
  checks: Record<string, CompatibilityCheck>
  cluster_info: Record<string, any>
  recommendations: string[]
  cluster_id: string
  cluster_name: string
  checked_at: string
}

// Cluster Health / High Availability interfaces
export interface ClusterHealthAgent {
  agent_id: string
  node_name: string
  connected_at: string
  last_heartbeat: string
  is_leader: boolean
}

export interface ClusterHealthWarning {
  code: string
  message: string
  severity: 'critical' | 'warning' | 'info'
}

export interface ClusterHealthResponse {
  cluster_id: string
  cluster_name: string
  status: 'healthy' | 'degraded' | 'offline' | 'unknown'
  ha_enabled: boolean
  agents: {
    connected: number
    total_nodes: number
    coverage: number
    leader: ClusterHealthAgent | null
    list: ClusterHealthAgent[]
  }
  warnings: ClusterHealthWarning[]
  recommendations: string[]
}

// Cluster Checklist interfaces
export interface ClusterChecklistItem {
  key: string
  label: string
  passed: boolean
  message: string
  details?: Record<string, any>
}

export interface ClusterChecklistResponse {
  cluster_id: string
  items: ClusterChecklistItem[]
  passed_count: number
  total_count: number
  all_passed: boolean
}

// =============================================================================
// Proxmox Cluster Settings interfaces
// =============================================================================

// Cluster Options
export interface ClusterOptions {
  migration?: string // migration type: secure, insecure
  migration_network?: string
  migration_unsecure?: number
  console?: string // console type: applet, vv, html5
  keyboard?: string // keyboard layout
  language?: string
  http_proxy?: string
  email_from?: string
  mac_prefix?: string
  bwlimit?: string // bandwidth limits
  ha?: Record<string, any>
  u2f?: Record<string, any>
  webauthn?: Record<string, any>
  next_id?: Record<string, any>
  description?: string
}

// Firewall Options
export interface ClusterFirewallOptions {
  enable?: number // 0 or 1
  policy_in?: string // ACCEPT, DROP, REJECT
  policy_out?: string
  log_ratelimit?: string
  ebtables?: number
}

// Firewall Rule
export interface ClusterFirewallRule {
  pos?: number
  type: string // in, out, group
  action: string // ACCEPT, DROP, REJECT
  enable?: number
  source?: string
  dest?: string
  proto?: string
  sport?: string
  dport?: string
  comment?: string
  macro?: string
  iface?: string
  log?: string
  digest?: string
}

// Firewall Alias
export interface FirewallAlias {
  name: string
  cidr: string
  comment?: string
  digest?: string
}

// Firewall IP Set
export interface FirewallIPSet {
  name: string
  comment?: string
  digest?: string
}

export interface IPSetEntry {
  cidr: string
  comment?: string
  nomatch?: number
  digest?: string
}

// HA interfaces
export interface HAStatus {
  current: any[]
  manager: Record<string, any>
}

export interface HAGroup {
  group: string
  nodes: string
  comment?: string
  nofailback?: number
  restricted?: number
  type?: string
  digest?: string
}

export interface HAResource {
  sid: string
  type?: string // vm, ct
  state?: string // started, stopped, enabled, disabled, ignored
  group?: string
  max_restart?: number
  max_relocate?: number
  comment?: string
  digest?: string
}

// Backup Schedule
export interface BackupSchedule {
  id?: string
  enabled?: number
  schedule?: string // cron or day-of-week format
  storage?: string
  mode?: string // snapshot, suspend, stop
  compress?: string // 0, gzip, lzo, zstd
  vmid?: string // comma-separated VMIDs, or 'all'
  node?: string
  pool?: string
  mailnotification?: string
  mailto?: string
  exclude?: string
  dow?: string // day of week
  starttime?: string
  maxfiles?: number
  'prune-backups'?: string
  notes_template?: string
  'repeat-missed'?: number
  comment?: string
}

// Replication Job
export interface ReplicationJob {
  id?: string
  type?: string // local
  source?: string
  target?: string
  guest?: number // VMID
  schedule?: string // cron
  rate?: number // rate limit MB/s
  comment?: string
  disable?: number
  remove_job?: string
  digest?: string
}

// Ceph interfaces
export interface CephStatus {
  health: {
    status: string // HEALTH_OK, HEALTH_WARN, HEALTH_ERR
    checks?: Record<string, { severity: string; summary: { message: string } }>
  }
  pgmap?: {
    num_pgs: number
    data_bytes: number
    bytes_used: number
    bytes_avail: number
    bytes_total: number
    read_bytes_sec?: number
    write_bytes_sec?: number
    read_op_per_sec?: number
    write_op_per_sec?: number
    pgs_by_state: Array<{ state_name: string; count: number }>
  }
  osdmap?: { osdmap: { num_osds: number; num_up_osds: number; num_in_osds: number } }
  monmap?: { mons: Array<{ name: string; rank: number; addr: string }> }
  quorum_names?: string[]
}

export interface CephMonitor {
  name: string
  addr?: string
  host?: string
  rank?: number
}

export interface CephMDS {
  name: string
  host?: string
  addr?: string
  rank?: number
  state?: string
}

export interface CephOSD {
  id: number
  name: string
  host?: string
  status?: string
  in?: number
  class?: string
  crush_weight?: number
  type?: string
  children?: CephOSD[]
}

export interface CephPool {
  pool: number
  pool_name: string
  size: number
  min_size: number
  pg_num: number
  crush_rule: number
  crush_rule_name?: string
  percent_used?: number
  bytes_used?: number
  application_metadata?: Record<string, any>
}

export interface CephDisk {
  devpath: string
  model?: string
  serial?: string
  size: number
  vendor?: string
  type: string
  used?: string
}

export interface CephInitParams {
  node: string
  network: string
  'cluster-network'?: string
  size?: number
  min_size?: number
}

export interface CephOSDCreateParams {
  node: string
  dev: string
  db_dev?: string
  wal_dev?: string
  encrypted?: boolean
  'crush-device-class'?: string
}

export interface CephPoolCreateParams {
  name: string
  node: string
  pg_num?: number
  size?: number
  min_size?: number
  application?: string
  add_storages?: boolean
}

// =============================================================================
// Clusters API functions
// =============================================================================
export const clustersApi = {
  // Get all clusters
  getClusters: async (): Promise<Cluster[]> => {
    const response = await api.get('/api/v1/clusters/')
    return response.data
  },

  // Get cluster by ID
  getCluster: async (clusterId: string): Promise<Cluster> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}`)
    return response.data
  },

  // Create new cluster via agent (no credentials needed)
  createClusterViaAgent: async (
    data: CreateClusterViaAgentRequest,
  ): Promise<ClusterCreationResponse> => {
    const response = await api.post('/api/v1/clusters/via-agent', data)
    return response.data
  },

  // Get registration token for an agent-mode cluster
  getClusterRegistrationToken: async (
    clusterId: string,
  ): Promise<ClusterRegistrationTokenResponse> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/registration-token`)
    return response.data
  },

  // Regenerate registration token for an agent-mode cluster
  regenerateClusterRegistrationToken: async (
    clusterId: string,
  ): Promise<ClusterRegistrationTokenResponse> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/registration-token/regenerate`)
    return response.data
  },

  // Update cluster
  updateCluster: async (clusterId: string, data: UpdateClusterRequest): Promise<Cluster> => {
    const response = await api.put(`/api/v1/clusters/${clusterId}`, data)
    return response.data
  },

  // Delete cluster
  deleteCluster: async (clusterId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/api/v1/clusters/${clusterId}`)
    return response.data
  },

  // Sync cluster with Proxmox (long operation)
  syncCluster: async (clusterId: string, force: boolean = false): Promise<SyncResponse> => {
    return await apiClient.postLong(`/api/v1/clusters/${clusterId}/sync?force=${force}`)
  },

  // Test cluster connection and run compatibility check
  testClusterConnection: async (clusterId: string): Promise<CompatibilityReport> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/test-connection`)
    return response.data
  },

  // Get cluster nodes
  getClusterNodes: async (clusterId: string): Promise<NodesResponse> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/nodes`)
    return response.data
  },

  // Get cluster VMs
  getClusterVMs: async (
    clusterId: string,
    node?: string,
    status?: string,
  ): Promise<VMsResponse> => {
    const params = new URLSearchParams()
    if (node) params.append('node', node)
    if (status) params.append('status', status)

    const query = params.toString() ? `?${params.toString()}` : ''
    const response = await api.get(`/api/v1/clusters/${clusterId}/vms${query}`)
    return response.data
  },

  // Get Proxmox templates from cluster
  getClusterProxmoxTemplates: async (
    clusterId: string,
    node?: string,
  ): Promise<ProxmoxTemplatesResponse> => {
    const params = new URLSearchParams()
    if (node) params.append('node', node)

    const query = params.toString() ? `?${params.toString()}` : ''
    const response = await api.get(`/api/v1/clusters/${clusterId}/templates/proxmox${query}`)
    return response.data
  },

  // Delete a template directly from Proxmox by VMID
  deleteProxmoxTemplate: async (clusterId: string, vmid: number): Promise<void> => {
    await api.delete(`/api/v1/clusters/${clusterId}/templates/proxmox/${vmid}`)
  },

  // Get cluster storage
  getClusterStorage: async (clusterId: string): Promise<StorageResponse> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/storage`)
    return response.data
  },

  // Get cluster monitoring data (MOCK - for compatibility)
  getClusterMonitoring: async (
    clusterId: string,
    timeframe: string = '1h',
    metric?: string,
  ): Promise<MonitoringResponse> => {
    const params = new URLSearchParams({ timeframe })
    if (metric) params.append('metric', metric)

    const response = await api.get(`/api/v1/clusters/${clusterId}/monitoring?${params.toString()}`)
    return response.data
  },

  // Get cluster monitoring data from InfluxDB (REAL DATA)
  getClusterInfluxMonitoring: async (
    clusterId: string,
    timeRange: string = '-1h',
  ): Promise<MonitoringResponse> => {
    const params = new URLSearchParams({ time_range: timeRange })

    const response = await api.get(
      `/api/v1/metrics/clusters/${clusterId}/dashboard?${params.toString()}`,
    )
    return response.data
  },

  // Get cluster logs
  getClusterLogs: async (
    clusterId: string,
    level?: string,
    node?: string,
    service?: string,
    limit: number = 100,
  ): Promise<LogsResponse> => {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (level) params.append('level', level)
    if (node) params.append('node', node)
    if (service) params.append('service', service)

    const response = await api.get(`/api/v1/clusters/${clusterId}/logs?${params.toString()}`)

    return response.data
  },

  // Get real-time VM status (no caching) - for accurate status display
  getClusterVMsRealtimeStatus: async (clusterId: string): Promise<VMsResponse> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/vms/realtime-status`)
    return response.data
  },

  // Perform VM action
  performVMAction: async (
    clusterId: string,
    vmid: number,
    action: string,
  ): Promise<VMActionResponse> => {
    const response = await api.post(
      `/api/v1/clusters/${clusterId}/vms/${vmid}/action?action=${action}`,
    )
    return response.data
  },

  // Delete VM (exige confirmação por OTP; pode ser soft delete dependendo do cluster)
  deleteVM: async (
    clusterId: string,
    vmid: number,
    opts: {
      otp_temp_token: string
      otp_code: string
      force?: boolean
      immediate?: boolean
    },
  ): Promise<{
    success: boolean
    message: string
    vmid: number
    task_id?: string
    soft_delete?: boolean
    force_used?: boolean
  }> => {
    const response = await api.delete(`/api/v1/clusters/${clusterId}/vms/${vmid}`, {
      data: {
        otp_temp_token: opts.otp_temp_token,
        otp_code: opts.otp_code,
        force: opts.force ?? false,
        immediate: opts.immediate ?? false,
      },
    })
    return response.data
  },

  // Cancela o soft delete (restaura a VM)
  cancelVmDeletion: async (
    clusterId: string,
    vmid: number,
  ): Promise<{ success: boolean; message: string; vmid: number; task_id?: string }> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/vms/${vmid}/cancel-deletion`)
    return response.data
  },

  // Get cluster health / High Availability status
  getClusterHealth: async (clusterId: string): Promise<ClusterHealthResponse> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/health`)
    return response.data
  },

  // Get cluster network configuration
  getClusterNetworks: async (clusterId: string): Promise<NetworksResponse> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/networks`)
    return response.data
  },

  // ==========================================================================
  // Proxmox Cluster Options
  // ==========================================================================

  getClusterOptions: async (clusterId: string): Promise<ClusterOptions> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/options`)
    return response.data?.data ?? response.data
  },

  updateClusterOptions: async (clusterId: string, data: Partial<ClusterOptions>): Promise<any> => {
    const response = await api.put(`/api/v1/clusters/${clusterId}/proxmox/options`, data)
    return response.data
  },

  // ==========================================================================
  // Proxmox Cluster Firewall
  // ==========================================================================

  getClusterFirewallOptions: async (clusterId: string): Promise<ClusterFirewallOptions> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/firewall/options`)
    return response.data?.data ?? response.data
  },

  updateClusterFirewallOptions: async (
    clusterId: string,
    data: Partial<ClusterFirewallOptions>,
  ): Promise<any> => {
    const response = await api.put(`/api/v1/clusters/${clusterId}/proxmox/firewall/options`, data)
    return response.data
  },

  getClusterFirewallRules: async (clusterId: string): Promise<ClusterFirewallRule[]> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/firewall/rules`)
    return response.data?.data ?? response.data
  },

  createClusterFirewallRule: async (
    clusterId: string,
    rule: Partial<ClusterFirewallRule>,
  ): Promise<any> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/proxmox/firewall/rules`, rule)
    return response.data
  },

  updateClusterFirewallRule: async (
    clusterId: string,
    pos: number,
    rule: Partial<ClusterFirewallRule>,
  ): Promise<any> => {
    const response = await api.put(
      `/api/v1/clusters/${clusterId}/proxmox/firewall/rules/${pos}`,
      rule,
    )
    return response.data
  },

  deleteClusterFirewallRule: async (clusterId: string, pos: number): Promise<any> => {
    const response = await api.delete(`/api/v1/clusters/${clusterId}/proxmox/firewall/rules/${pos}`)
    return response.data
  },

  getClusterFirewallAliases: async (clusterId: string): Promise<FirewallAlias[]> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/firewall/aliases`)
    return response.data?.data ?? response.data
  },

  createClusterFirewallAlias: async (
    clusterId: string,
    alias: Partial<FirewallAlias>,
  ): Promise<any> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/proxmox/firewall/aliases`, alias)
    return response.data
  },

  updateClusterFirewallAlias: async (
    clusterId: string,
    name: string,
    alias: Partial<FirewallAlias>,
  ): Promise<any> => {
    const response = await api.put(
      `/api/v1/clusters/${clusterId}/proxmox/firewall/aliases/${name}`,
      alias,
    )
    return response.data
  },

  deleteClusterFirewallAlias: async (clusterId: string, name: string): Promise<any> => {
    const response = await api.delete(
      `/api/v1/clusters/${clusterId}/proxmox/firewall/aliases/${name}`,
    )
    return response.data
  },

  getClusterFirewallIPSets: async (clusterId: string): Promise<FirewallIPSet[]> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/firewall/ipsets`)
    return response.data?.data ?? response.data
  },

  createClusterFirewallIPSet: async (
    clusterId: string,
    ipset: Partial<FirewallIPSet>,
  ): Promise<any> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/proxmox/firewall/ipsets`, ipset)
    return response.data
  },

  deleteClusterFirewallIPSet: async (clusterId: string, name: string): Promise<any> => {
    const response = await api.delete(
      `/api/v1/clusters/${clusterId}/proxmox/firewall/ipsets/${name}`,
    )
    return response.data
  },

  getIPSetContent: async (clusterId: string, name: string): Promise<IPSetEntry[]> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/firewall/ipsets/${name}`)
    return response.data?.data ?? response.data
  },

  addIPSetCIDR: async (
    clusterId: string,
    name: string,
    entry: Partial<IPSetEntry>,
  ): Promise<any> => {
    const response = await api.post(
      `/api/v1/clusters/${clusterId}/proxmox/firewall/ipsets/${name}/cidrs`,
      entry,
    )
    return response.data
  },

  deleteIPSetCIDR: async (clusterId: string, name: string, cidr: string): Promise<any> => {
    const response = await api.delete(
      `/api/v1/clusters/${clusterId}/proxmox/firewall/ipsets/${name}/cidrs/${encodeURIComponent(cidr)}`,
    )
    return response.data
  },

  // ==========================================================================
  // Proxmox HA (High Availability)
  // ==========================================================================

  getHAStatus: async (clusterId: string): Promise<HAStatus> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/ha/status`)
    return response.data?.data ?? response.data
  },

  getHAGroups: async (clusterId: string): Promise<HAGroup[]> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/ha/groups`)
    return response.data?.data ?? response.data
  },

  createHAGroup: async (clusterId: string, group: Partial<HAGroup>): Promise<any> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/proxmox/ha/groups`, group)
    return response.data
  },

  getHAGroup: async (clusterId: string, group: string): Promise<HAGroup> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/ha/groups/${group}`)
    return response.data?.data ?? response.data
  },

  updateHAGroup: async (clusterId: string, group: string, data: Partial<HAGroup>): Promise<any> => {
    const response = await api.put(`/api/v1/clusters/${clusterId}/proxmox/ha/groups/${group}`, data)
    return response.data
  },

  deleteHAGroup: async (clusterId: string, group: string): Promise<any> => {
    const response = await api.delete(`/api/v1/clusters/${clusterId}/proxmox/ha/groups/${group}`)
    return response.data
  },

  getHAResources: async (clusterId: string): Promise<HAResource[]> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/ha/resources`)
    return response.data?.data ?? response.data
  },

  createHAResource: async (clusterId: string, resource: Partial<HAResource>): Promise<any> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/proxmox/ha/resources`, resource)
    return response.data
  },

  getHAResource: async (clusterId: string, sid: string): Promise<HAResource> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/ha/resources/${sid}`)
    return response.data?.data ?? response.data
  },

  updateHAResource: async (
    clusterId: string,
    sid: string,
    data: Partial<HAResource>,
  ): Promise<any> => {
    const response = await api.put(
      `/api/v1/clusters/${clusterId}/proxmox/ha/resources/${sid}`,
      data,
    )
    return response.data
  },

  deleteHAResource: async (clusterId: string, sid: string): Promise<any> => {
    const response = await api.delete(`/api/v1/clusters/${clusterId}/proxmox/ha/resources/${sid}`)
    return response.data
  },

  // ==========================================================================
  // Proxmox Backup Schedules
  // ==========================================================================

  getBackupSchedules: async (clusterId: string): Promise<BackupSchedule[]> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/backup`)
    return response.data?.data ?? response.data
  },

  createBackupSchedule: async (
    clusterId: string,
    schedule: Partial<BackupSchedule>,
  ): Promise<any> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/proxmox/backup`, schedule)
    return response.data
  },

  getBackupSchedule: async (clusterId: string, id: string): Promise<BackupSchedule> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/backup/${id}`)
    return response.data?.data ?? response.data
  },

  updateBackupSchedule: async (
    clusterId: string,
    id: string,
    schedule: Partial<BackupSchedule>,
  ): Promise<any> => {
    const response = await api.put(`/api/v1/clusters/${clusterId}/proxmox/backup/${id}`, schedule)
    return response.data
  },

  deleteBackupSchedule: async (clusterId: string, id: string): Promise<any> => {
    const response = await api.delete(`/api/v1/clusters/${clusterId}/proxmox/backup/${id}`)
    return response.data
  },

  getBackupInfo: async (clusterId: string): Promise<any> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/backup-info`)
    return response.data?.data ?? response.data
  },

  // ==========================================================================
  // Proxmox Replication
  // ==========================================================================

  getReplicationJobs: async (clusterId: string): Promise<ReplicationJob[]> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/replication`)
    return response.data?.data ?? response.data
  },

  createReplicationJob: async (clusterId: string, job: Partial<ReplicationJob>): Promise<any> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/proxmox/replication`, job)
    return response.data
  },

  getReplicationJob: async (clusterId: string, id: string): Promise<ReplicationJob> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/replication/${id}`)
    return response.data?.data ?? response.data
  },

  updateReplicationJob: async (
    clusterId: string,
    id: string,
    job: Partial<ReplicationJob>,
  ): Promise<any> => {
    const response = await api.put(`/api/v1/clusters/${clusterId}/proxmox/replication/${id}`, job)
    return response.data
  },

  deleteReplicationJob: async (clusterId: string, id: string): Promise<any> => {
    const response = await api.delete(`/api/v1/clusters/${clusterId}/proxmox/replication/${id}`)
    return response.data
  },

  // ==========================================================================
  // Proxmox Ceph
  // ==========================================================================

  getCephStatus: async (clusterId: string): Promise<CephStatus> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/ceph/status`)
    return response.data?.data ?? response.data
  },

  getCephConfig: async (clusterId: string): Promise<string> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/ceph/config`)
    return response.data?.data ?? response.data
  },

  initCeph: async (clusterId: string, params: CephInitParams): Promise<any> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/proxmox/ceph/init`, params)
    return response.data
  },

  startCeph: async (clusterId: string, params?: { node?: string }): Promise<any> => {
    const response = await api.post(
      `/api/v1/clusters/${clusterId}/proxmox/ceph/start`,
      params || {},
    )
    return response.data
  },

  stopCeph: async (clusterId: string, params?: { node?: string }): Promise<any> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/proxmox/ceph/stop`, params || {})
    return response.data
  },

  getCephMonitors: async (clusterId: string): Promise<CephMonitor[]> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/ceph/mon`)
    return response.data?.data ?? response.data
  },

  createCephMonitor: async (
    clusterId: string,
    params: { node: string; 'mon-address'?: string },
  ): Promise<any> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/proxmox/ceph/mon`, params)
    return response.data
  },

  destroyCephMonitor: async (clusterId: string, monid: string): Promise<any> => {
    const response = await api.delete(`/api/v1/clusters/${clusterId}/proxmox/ceph/mon/${monid}`)
    return response.data
  },

  createCephManager: async (clusterId: string, params: { node: string }): Promise<any> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/proxmox/ceph/mgr`, params)
    return response.data
  },

  destroyCephManager: async (clusterId: string, mgrid: string): Promise<any> => {
    const response = await api.delete(`/api/v1/clusters/${clusterId}/proxmox/ceph/mgr/${mgrid}`)
    return response.data
  },

  getCephMDS: async (clusterId: string): Promise<CephMDS[]> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/ceph/mds`)
    return response.data?.data ?? response.data
  },

  getCephOSDs: async (clusterId: string): Promise<any> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/ceph/osd`)
    return response.data?.data ?? response.data
  },

  createCephOSD: async (clusterId: string, params: CephOSDCreateParams): Promise<any> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/proxmox/ceph/osd`, params)
    return response.data
  },

  destroyCephOSD: async (
    clusterId: string,
    osdid: number,
    cleanup?: boolean,
    force?: boolean,
  ): Promise<any> => {
    const params: Record<string, any> = {}
    if (cleanup !== undefined) params.cleanup = cleanup
    if (force !== undefined) params.force = force
    const response = await api.delete(`/api/v1/clusters/${clusterId}/proxmox/ceph/osd/${osdid}`, {
      params,
    })
    return response.data
  },

  setCephOSDIn: async (clusterId: string, osdid: number): Promise<any> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/proxmox/ceph/osd/${osdid}/in`)
    return response.data
  },

  setCephOSDOut: async (clusterId: string, osdid: number): Promise<any> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/proxmox/ceph/osd/${osdid}/out`)
    return response.data
  },

  getCephPools: async (clusterId: string): Promise<CephPool[]> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/ceph/pools`)
    return response.data?.data ?? response.data
  },

  createCephPool: async (clusterId: string, params: CephPoolCreateParams): Promise<any> => {
    const response = await api.post(`/api/v1/clusters/${clusterId}/proxmox/ceph/pools`, params)
    return response.data
  },

  deleteCephPool: async (
    clusterId: string,
    name: string,
    removeStorages?: boolean,
    force?: boolean,
  ): Promise<any> => {
    const params: Record<string, any> = {}
    if (removeStorages !== undefined) params.remove_storages = removeStorages
    if (force !== undefined) params.force = force
    const response = await api.delete(`/api/v1/clusters/${clusterId}/proxmox/ceph/pools/${name}`, {
      params,
    })
    return response.data
  },

  getCephDisks: async (clusterId: string, node: string): Promise<CephDisk[]> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/proxmox/ceph/disks`, {
      params: { node },
    })
    return response.data?.data ?? response.data
  },

  // Get cluster configuration checklist
  getClusterChecklist: async (clusterId: string): Promise<ClusterChecklistResponse> => {
    const response = await api.get(`/api/v1/clusters/${clusterId}/checklist`)
    return response.data
  },
}
